import {
  Button,
  Chevron,
  Edit,
  EmptyState,
  Icon,
  NoDataIcon,
  Select,
  Spinner,
  Table,
  ThreeDots
} from '@lmc/ui-kit'
import { EmptyStateDescription } from '@lmc/ui-kit/src/dataDisplay/EmptyState'
import { Text } from '@lmc/ui-kit/src/general'
import { Dropdown } from 'antd'
import { getNominative } from 'common/utils'
import { useAbilityAccess } from 'components/AccessControl'
import ExternalLink from 'components/icons/ExternalLink'
import dayjs from 'dayjs'
import useResidentNeeds, { type ResidentNeedV6 } from 'hooks/useResidentNeeds'
import type React from 'react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import styled from 'styled-components'

const HELP_LINK =
  'https://support.logmycare.co.uk/en/articles/6987558-how-to-add-needs-for-your-service-users'

// ── CarePlanSpoiler ───────────────────────────────────────────────────────────

type CarePlanSpoilerProps = {
  carePlans: Array<{ id: string; name: string }>
  residentId: string
  carePlanNominative?: string
  hasNotesAbove?: boolean
}

const CarePlanSpoiler = ({
  carePlans,
  residentId,
  carePlanNominative,
  hasNotesAbove
}: CarePlanSpoilerProps): React.ReactElement => {
  const [isOpen, setIsOpen] = useState(false)
  const count = carePlans.length
  const pluralName = (carePlanNominative ?? 'care plans').toLowerCase()
  const singularName = getNominative(
    carePlanNominative,
    'care plan'
  ).toLowerCase()
  const label = `In ${count} ${count === 1 ? singularName : pluralName}`

  return (
    <SpoilerWrapper $hasNotesAbove={hasNotesAbove}>
      <SpoilerToggle onClick={() => setIsOpen((prev) => !prev)}>
        <Chevron direction={isOpen ? 'up' : 'down'} />
        {label}
      </SpoilerToggle>
      {isOpen && (
        <SpoilerList>
          {carePlans.map((cp) => (
            <SpoilerListItem key={cp.id}>
              <Link to={`/residents/${residentId}/care-plans/${cp.id}`}>
                {cp.name}
              </Link>
            </SpoilerListItem>
          ))}
        </SpoilerList>
      )}
    </SpoilerWrapper>
  )
}

const SORT_OPTIONS = [
  { sortKey: 'newest', labelKey: 'skillsAndNeeds.sortNewestFirst' },
  { sortKey: 'oldest', labelKey: 'skillsAndNeeds.sortOldestFirst' },
  { sortKey: 'az', labelKey: 'skillsAndNeeds.sortNameAZ' },
  { sortKey: 'za', labelKey: 'skillsAndNeeds.sortNameZA' }
]

const sortNeeds = (
  needs: ResidentNeedV6[],
  sortKey: string
): ResidentNeedV6[] => {
  const sorted = [...needs]
  switch (sortKey) {
    case 'newest':
      return sorted.sort(
        (a, b) =>
          dayjs(b.addedOn ?? 0).valueOf() - dayjs(a.addedOn ?? 0).valueOf()
      )
    case 'oldest':
      return sorted.sort(
        (a, b) =>
          dayjs(a.addedOn ?? 0).valueOf() - dayjs(b.addedOn ?? 0).valueOf()
      )
    case 'az':
      return sorted.sort((a, b) => a.need.name.localeCompare(b.need.name))
    case 'za':
      return sorted.sort((a, b) => b.need.name.localeCompare(a.need.name))
    default:
      return sorted
  }
}

type ResidentNeedsListProps = {
  residentId: string
  onEdit: (need: ResidentNeedV6) => void
  onDelete: (need: ResidentNeedV6) => void
  onAssign: () => void
  testId?: string
}

const ResidentNeedsList = ({
  residentId,
  onEdit,
  onDelete,
  onAssign,
  testId
}: ResidentNeedsListProps): React.ReactElement => {
  const { t } = useTranslation()
  const { needs, pagination, isLoading } = useResidentNeeds({ id: residentId })
  const [sortIndex, setSortIndex] = useState(0)

  const carePlanNominative = useSelector(
    (state: { global: { home: { preferredCarePlansNominative?: string } } }) =>
      state.global.home.preferredCarePlansNominative
  )

  const canManageNeeds = useAbilityAccess([
    { i: 'update', this: 'ResidentNeed' }
  ])

  const sortingOptions = SORT_OPTIONS.map((opt, index) => ({
    label: t(opt.labelKey),
    value: String(index)
  }))

  const sortedNeeds = useMemo(
    () => sortNeeds(needs, SORT_OPTIONS[sortIndex].sortKey),
    [needs, sortIndex]
  )

  if (isLoading) {
    return <Spinner style={{ minHeight: 200 }} />
  }

  if (needs.length === 0) {
    return (
      <EmptyState
        icon={<NoDataIcon />}
        title=""
        description={
          <BlackDescription level={1}>
            {t('skillsAndNeeds.assignNeedEmptyState')}{' '}
            <a href={HELP_LINK} target="_blank" rel="noreferrer">
              {t('skillsAndNeeds.assignNeedEmptyStateLink')}
              <ExternalLinkWrapper>
                <ExternalLink />
              </ExternalLinkWrapper>
            </a>
          </BlackDescription>
        }
      >
        {canManageNeeds && (
          <Button
            onClick={onAssign}
            icon={<Icon name="PlusOutlined" />}
            variant="primary"
            data-testid={
              testId
                ? `${testId}-needs-empty-assign-button`
                : 'needs-empty-assign-button'
            }
          >
            {t('skillsAndNeeds.assignNewNeedButton')}
          </Button>
        )}
      </EmptyState>
    )
  }

  const columns = [
    {
      title: t('skillsAndNeeds.addedDateColumn'),
      dataIndex: 'addedOn',
      key: 'addedOn',
      width: '20%',
      render: (value: string) =>
        value ? dayjs(value).format('DD-MM-YYYY') : '-'
    },
    {
      title: t('skillsAndNeeds.needColumn'),
      key: 'need',
      width: '35%',
      render: (_: unknown, record: ResidentNeedV6) => record.need.name
    },
    {
      title: t('skillsAndNeeds.notesColumn'),
      key: 'notes',
      width: '35%',
      render: (_: unknown, record: ResidentNeedV6) => {
        const carePlans = record.carePlans ?? []
        const hasNotes = !!record.notes
        const hasCarePlans = carePlans.length > 0
        return (
          <div>
            {hasNotes && <div>{record.notes}</div>}
            {!hasNotes && !hasCarePlans && (
              <StyledNA>{t('skillsAndNeeds.notesNA')}</StyledNA>
            )}
            {hasCarePlans && (
              <CarePlanSpoiler
                carePlans={carePlans}
                residentId={residentId}
                carePlanNominative={carePlanNominative}
                hasNotesAbove={hasNotes}
              />
            )}
          </div>
        )
      }
    },
    ...(canManageNeeds
      ? [
          {
            title: t('skillsAndNeeds.actionsColumn'),
            key: 'actions',
            width: 160,
            render: (_: unknown, record: ResidentNeedV6) => (
              <StyledActionsCell
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <StyledEditButton
                  variant="default"
                  onClick={() => onEdit(record)}
                >
                  <Edit />
                  {t('skillsAndNeeds.editNeed')}
                </StyledEditButton>
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'delete',
                        danger: true,
                        label: t('skillsAndNeeds.deleteNeedAction'),
                        onClick: () => onDelete(record)
                      }
                    ],
                    style: { borderRadius: 20 }
                  }}
                  trigger={['click']}
                  placement="bottomRight"
                  align={{ offset: [-4, 16] }}
                >
                  <StyledDotsButton>
                    <ThreeDots />
                  </StyledDotsButton>
                </Dropdown>
              </StyledActionsCell>
            )
          }
        ]
      : [])
  ]

  const dataSource = sortedNeeds.map((need) => ({ ...need, key: need.id }))

  return (
    <StyledTableContainer>
      <StyledTableHeader>
        <StyledNeedsCount level={1}>
          {t('skillsAndNeeds.residentNeedsCount', {
            current: needs.length,
            total: pagination?.totalItems ?? needs.length
          })}
        </StyledNeedsCount>
        <StyledSortWrapper>
          <StyledSortLabel weight="bold">
            {t('skillsAndNeeds.sortBy')}
          </StyledSortLabel>
          <StyledSortSelect
            placeholder={
              <StyledOptionText weight="regular">
                {sortingOptions[0].label}
              </StyledOptionText>
            }
            suffixIcon={<Chevron direction="down" />}
            onChange={(value) => setSortIndex(Number(value))}
          >
            {sortingOptions.map((option) => (
              <Select.Option
                key={option.value}
                value={option.value}
                label={option.label}
              >
                <StyledOptionText weight="regular">
                  {option.label}
                </StyledOptionText>
              </Select.Option>
            ))}
          </StyledSortSelect>
        </StyledSortWrapper>
      </StyledTableHeader>
      <Table dataSource={dataSource} columns={columns} pagination={false} />
    </StyledTableContainer>
  )
}

export default ResidentNeedsList

const ExternalLinkWrapper = styled.span`
  display: inline-block;
  vertical-align: middle;
  margin-left: 4px;
  padding-top: 1px;
`

const BlackDescription = styled(EmptyStateDescription)`
  color: ${({ theme }) => theme.color.neutral.color3};

  a {
    text-decoration: underline;
  }
`

const StyledNA = styled.span`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const StyledTableContainer = styled.div`
  overflow: auto;
  margin-top: 12px;

  .ant-table-thead > tr > th {
    padding-top: 8px !important;
    padding-bottom: 8px !important;
    color: ${({ theme }) => theme.color.neutral.color4} !important;
    font-weight: 500 !important;
  }

  .ant-table-tbody > tr:nth-child(odd) > td {
    background: ${({ theme }) => theme.color.neutral.color11};
  }

  .ant-table-tbody > tr:nth-child(even) > td {
    background: ${({ theme }) => theme.color.neutral.color10};
  }

  .ant-table-tbody > tr > td {
    color: ${({ theme }) => theme.color.neutral.color3} !important;
    font-weight: 500 !important;
    vertical-align: top !important;
    font-size: 15px !important;
  }
`

const StyledTableHeader = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  margin-bottom: 16px;
`

const StyledNeedsCount = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4} !important;
`

const StyledSortWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const StyledSortLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color3};
`

const StyledSortSelect = styled(Select)`
  width: 290px;
` as typeof Select

const StyledOptionText = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color4};
`

const StyledActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const StyledDotsButton = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`

const StyledEditButton = styled(Button)`
  gap: 2px;
  padding: 6px 15px;
  margin-right: 8px;
`

const SpoilerWrapper = styled.div<{ $hasNotesAbove?: boolean }>`
  ${({ $hasNotesAbove }) => $hasNotesAbove && 'margin-top: 8px;'}
`

const SpoilerToggle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  font-size: 13px;
  color: ${({ theme }) => theme.color.status.primary};
  user-select: none;

  &:hover {
    color: ${({ theme }) => theme.color.hover.primary};
  }

  svg {
    width: 10px;
    height: 10px;
  }
`

const SpoilerList = styled.ul`
  margin: 6px 0 0 0;
  padding-left: 18px;
  list-style: disc;
`

const SpoilerListItem = styled.li`
  font-size: 13px;

  a {
    color: ${({ theme }) => theme.color.status.primary};
    text-decoration: underline;

    &:hover {
      color: ${({ theme }) => theme.color.hover.primary};
    }
  }
`
